import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { interviewAPI, questionAPI, groupAPI, authAPI, tagAPI, categoryAPI } from '../services/api';
import Select from 'react-select';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { Modal, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

function ScheduleInterview() {
  const navigate = useNavigate();
  const { id } = useParams(); // Get interview ID from URL for edit mode
  const isEditMode = Boolean(id);
  const { showToast } = useToast();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    duration: 30,
    instructions: '',
    assignmentType: 'students',
    assignedTo: [],
    assignedGroup: null,
    allowMultipleTakes: false,
    maxAttempts: 0, // 0 = unlimited
    showHints: false,
    allowStudentDownload: false,
    showAIEvaluation: false,
    requireVideoRecording: true,
    difficulty: 'beginner',
  });

  const [filters, setFilters] = useState({
    search: '',
    tags: '',
    difficulty: '',
    questionType: '',
    category: ''
  });

  // Track which questions have follow-up enabled
  const [followUpEnabled, setFollowUpEnabled] = useState({});
  
  // Track per-question follow-up range
  const [followUpRanges, setFollowUpRanges] = useState({});

  // Track nested follow-up settings
  const [nestedFollowUpEnabled, setNestedFollowUpEnabled] = useState({});
  const [nestedFollowUpRanges, setNestedFollowUpRanges] = useState({});

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);

  const toggleFollowUp = (questionId) => {
    setFollowUpEnabled(prev => {
      const newEnabled = !prev[questionId];
      // Initialize range when enabling
      if (newEnabled && !followUpRanges[questionId]) {
        setFollowUpRanges(prevRanges => ({
          ...prevRanges,
          [questionId]: { min: 1, max: 2 }
        }));
      }
      return {
        ...prev,
        [questionId]: newEnabled
      };
    });
  };

  const updateFollowUpRange = (questionId, field, value) => {
    setFollowUpRanges(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value
      }
    }));
  };

  const toggleNestedFollowUp = (questionId) => {
    setNestedFollowUpEnabled(prev => {
      const newEnabled = !prev[questionId];
      // Initialize range when enabling
      if (newEnabled && !nestedFollowUpRanges[questionId]) {
        setNestedFollowUpRanges(prevRanges => ({
          ...prevRanges,
          [questionId]: { min: 1, max: 1 }
        }));
      }
      return {
        ...prev,
        [questionId]: newEnabled
      };
    });
  };

  const updateNestedFollowUpRange = (questionId, field, value) => {
    setNestedFollowUpRanges(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value
      }
    }));
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newQuestions = [...selectedQuestions];
    const draggedQuestion = newQuestions[draggedIndex];
    newQuestions.splice(draggedIndex, 1);
    newQuestions.splice(dropIndex, 0, draggedQuestion);
    
    setSelectedQuestions(newQuestions);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  useEffect(() => {
    // Check if user is admin
    if (user && user.role !== 'admin') {
      showToast('Only admins can schedule interviews', 'error');
      navigate('/interviews');
      return;
    }
    
    fetchInitialData();
    if (isEditMode) {
      fetchInterviewData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInterviewData = async () => {
    setLoading(true);
    try {
      const response = await interviewAPI.getInterview(id);
      const interview = response.data.interview;
      
      // Populate form data
      // Convert UTC dates to local datetime-local format
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        // Get local date/time components
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };
      
      setFormData({
        title: interview.title || '',
        description: interview.description || '',
        startDate: formatDateForInput(interview.startDate),
        endDate: formatDateForInput(interview.endDate),
        duration: interview.duration || 30,
        instructions: interview.instructions || '',
        assignmentType: interview.assignedGroup ? 'group' : 'students',
        assignedTo: interview.assignedTo?.map(s => s._id || s) || [],
        assignedGroup: interview.assignedGroup?._id || interview.assignedGroup || null,
        allowMultipleTakes: interview.allowMultipleTakes || false,
        maxAttempts: interview.maxAttempts !== undefined ? interview.maxAttempts : 0,
        showHints: interview.showHints || false,
        allowStudentDownload: interview.allowStudentDownload || false,
        showAIEvaluation: interview.showAIEvaluation || false,
        requireVideoRecording: interview.requireVideoRecording !== undefined ? interview.requireVideoRecording : true,
        difficulty: interview.difficulty || 'beginner',
      });
      
      // Populate selected questions and follow-up settings
      if (interview.questions && interview.questions.length > 0) {
        const questionsData = interview.questions.map(q => q.questionId || q);
        setSelectedQuestions(questionsData);
        
        // Load per-question follow-up settings
        const followUpEnabledMap = {};
        const followUpRangesMap = {};
        const nestedFollowUpEnabledMap = {};
        const nestedFollowUpRangesMap = {};
        
        interview.questions.forEach(q => {
          const qId = (q.questionId && q.questionId._id) || q.questionId || q._id;
          if (q.enableFollowUp) {
            followUpEnabledMap[qId] = true;
            followUpRangesMap[qId] = {
              min: q.minFollowUpQuestions ?? 1,
              max: q.maxFollowUpQuestions ?? 2
            };
          }
          if (q.enableNestedFollowUp) {
            nestedFollowUpEnabledMap[qId] = true;
            nestedFollowUpRangesMap[qId] = {
              min: q.minNestedFollowUpQuestions ?? 1,
              max: q.maxNestedFollowUpQuestions ?? 1
            };
          }
        });
        setFollowUpEnabled(followUpEnabledMap);
        setFollowUpRanges(followUpRangesMap);
        setNestedFollowUpEnabled(nestedFollowUpEnabledMap);
        setNestedFollowUpRanges(nestedFollowUpRangesMap);
      }
    } catch (error) {
      console.error('Error fetching interview:', error);
      alert('Failed to load interview data');
    }
    setLoading(false);
  };

  const fetchInitialData = async () => {
    setLoadingStudents(true);
    try {
      console.log('Fetching initial data...');
      console.log('API Base URL:', window.location.origin);
      
      // Fetch them separately and handle errors independently
      let groupsData = [];
      let studentsData = [];
      let tagsData = [];
      let categoriesData = [];
      
      try {
        const groupsRes = await groupAPI.getMyGroups();
        console.log('✓ Groups loaded:', groupsRes.data);
        groupsData = groupsRes.data.groups || [];
      } catch (error) {
        console.error('✗ Groups failed:', error.response?.data || error.message);
        showToast('Failed to load groups', 'error');
      }
      
      try {
        const studentsRes = await authAPI.getAllStudents(1, 1000); // Get all students for interview scheduling
        console.log('✓ Students loaded:', studentsRes.data);
        studentsData = studentsRes.data.students || [];
      } catch (error) {
        console.error('✗ Students failed:', error.response?.data || error.message);
        
        // Provide specific error messages based on the response
        if (error.response?.status === 403) {
          showToast('Access denied: Admin privileges required to view students', 'error');
        } else if (error.response?.status === 401) {
          showToast('Authentication failed: Please log in again', 'error');
        } else if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
          showToast('Network error: Backend server may not be running', 'error');
        } else {
          showToast('Failed to load students: ' + (error.response?.data?.message || error.message), 'error');
        }
      }
      
      try {
        const tagsRes = await tagAPI.getAllTags();
        console.log('✓ Tags loaded:', tagsRes.data);
        tagsData = tagsRes.data.tags || [];
      } catch (error) {
        console.error('✗ Tags failed (non-critical):', error.response?.data || error.message);
        // Tags are optional, don't show error toast
      }
      
      try {
        const categoriesRes = await categoryAPI.getAllCategories();
        console.log('✓ Categories loaded:', categoriesRes.data);
        categoriesData = categoriesRes.data.categories || [];
      } catch (error) {
        console.error('✗ Categories failed (non-critical):', error.response?.data || error.message);
        // Categories are optional, don't show error toast
      }
      
      console.log('Setting state with:', { 
        groups: groupsData.length, 
        students: studentsData.length, 
        tags: tagsData.length,
        categories: categoriesData.length 
      });
      
      setGroups(groupsData);
      setStudents(studentsData);
      setTags(tagsData);
      setCategories(categoriesData);
      
      console.log('Data loading complete');
      console.log('Final state - Groups:', groupsData.length, 'Students:', studentsData.length);
      
      if (studentsData.length === 0 && groupsData.length === 0) {
        showToast('No students or groups found. Please add students first.', 'warning');
      }
    } catch (error) {
      console.error('Unexpected error in fetchInitialData:', error);
      showToast('An unexpected error occurred', 'error');
    } finally {
      setLoadingStudents(false);
    }
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const response = await questionAPI.getQuestions(1, 100, filters);
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
    setLoading(false);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleTagFilterChange = (selectedOptions) => {
    const tagString = selectedOptions ? selectedOptions.map(opt => opt.value).join(',') : '';
    setFilters(prev => ({ ...prev, tags: tagString }));
  };

  const handleCategoryFilterChange = (selectedOptions) => {
    const categoryString = selectedOptions ? selectedOptions.map(opt => opt.value).join(',') : '';
    setFilters(prev => ({ ...prev, category: categoryString }));
  };

  const handleQuestionToggle = (question) => {
    setSelectedQuestions(prev => {
      const exists = prev.find(q => q._id === question._id);
      if (exists) {
        return prev.filter(q => q._id !== question._id);
      } else {
        return [...prev, question];
      }
    });
  };

  const handleSelectByTags = async () => {
    if (!filters.tags) {
      alert('Please select at least one tag');
      return;
    }
    
    setLoading(true);
    try {
      const response = await questionAPI.searchByTags(
        filters.tags.split(','),
        filters.difficulty || null,
        50
      );
      const fetchedQuestions = response.data.questions || [];
      setSelectedQuestions(fetchedQuestions);
      alert(`${fetchedQuestions.length} questions selected based on tags`);
    } catch (error) {
      console.error('Error selecting by tags:', error);
      alert('Failed to select questions by tags');
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAssignmentTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      assignmentType: type,
      assignedTo: [],
      assignedGroup: null
    }));
  };

  const handleStudentSelection = (selectedOptions) => {
    const studentIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setFormData(prev => ({ ...prev, assignedTo: studentIds }));
  };

  const handleGroupSelection = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      assignedGroup: selectedOption ? selectedOption.value : null
    }));
  };

  const handleNext = () => {
    if (step === 1) {
      if (!formData.title || !formData.startDate || !formData.endDate) {
        alert('Please fill in all required fields');
        return;
      }
      if (formData.assignmentType === 'students' && formData.assignedTo.length === 0) {
        alert('Please select at least one student');
        return;
      }
      if (formData.assignmentType === 'group' && !formData.assignedGroup) {
        alert('Please select a group');
        return;
      }
      if (new Date(formData.startDate) >= new Date(formData.endDate)) {
        alert('End date must be after start date');
        return;
      }
      setStep(2);
      fetchQuestions();
    } else if (step === 2) {
      if (selectedQuestions.length === 0) {
        alert('Please select at least one question');
        return;
      }
      setStep(3);
    }
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Convert datetime-local to proper Date objects, which will be serialized to ISO (UTC) by axios
      // The backend will receive ISO strings and store them correctly
      const interviewData = {
        ...formData,
        // Convert to Date object - JavaScript will interpret datetime-local as local time
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
        questions: selectedQuestions.map((q, index) => ({
          questionId: q._id,
          order: index,
          enableFollowUp: followUpEnabled[q._id] || false,
          minFollowUpQuestions: followUpEnabled[q._id] ? (followUpRanges[q._id]?.min ?? 1) : 1,
          maxFollowUpQuestions: followUpEnabled[q._id] ? (followUpRanges[q._id]?.max ?? 2) : 2,
          enableNestedFollowUp: nestedFollowUpEnabled[q._id] || false,
          minNestedFollowUpQuestions: nestedFollowUpEnabled[q._id] ? (nestedFollowUpRanges[q._id]?.min ?? 1) : 1,
          maxNestedFollowUpQuestions: nestedFollowUpEnabled[q._id] ? (nestedFollowUpRanges[q._id]?.max ?? 1) : 1
        })),
        tags: filters.tags ? filters.tags.split(',') : []
      };
      
      // Clean up assignment fields based on assignment type
      // Only send the relevant field to avoid confusion
      delete interviewData.assignmentType; // Remove frontend-only field
      
      if (formData.assignmentType === 'group') {
        // When assigning to group, only send assignedGroup
        // The backend will populate assignedTo from the group
        delete interviewData.assignedTo;
      } else {
        // When assigning to students directly, send assignedTo and clear assignedGroup
        interviewData.assignedGroup = null;
      }
      
      console.log('Submitting interview with dates:', {
        startDateInput: formData.startDate,
        endDateInput: formData.endDate,
        startDateISO: interviewData.startDate,
        endDateISO: interviewData.endDate,
        assignmentType: formData.assignmentType,
        assignedGroup: interviewData.assignedGroup,
        assignedTo: interviewData.assignedTo
      });

      if (isEditMode) {
        await interviewAPI.updateInterview(id, interviewData);
        alert('Interview updated successfully!');
      } else {
        await interviewAPI.createInterview(interviewData);
        alert('Interview scheduled successfully!');
      }
      navigate('/interviews');
    } catch (error) {
      console.error('Error saving interview:', error);
      alert(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} interview`);
    }
    setLoading(false);
  };

  const studentOptions = students.map(s => ({ value: s._id, label: `${s.name} (${s.email})` }));
  const groupOptions = groups.map(g => ({ value: g._id, label: g.name }));
  const tagOptions = tags; // Tags are already in { value, label } format from the API
  const categoryOptions = categories.map(c => ({ value: c._id, label: c.name }));
  
  console.log('Render - students:', students.length, 'studentOptions:', studentOptions.length);
  console.log('Student options:', studentOptions);

  return (
    <div className="container">
      <div className="card">
        {loading && isEditMode && step === 1 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
            Loading interview data...
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>
                {isEditMode ? 'Edit Interview' : 'Schedule Interview'}
              </h2>
              <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
                {isEditMode ? 'Update interview details and questions' : 'Create and schedule interviews for students'}
              </p>
            </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px', background: '#f7fafc', padding: '16px', borderRadius: '8px' }}>
          <div style={{ flex: 1, textAlign: 'center', padding: '12px', borderRadius: '6px', background: step >= 1 ? '#667eea' : '#e2e8f0', color: step >= 1 ? 'white' : '#718096', fontWeight: 600, fontSize: '14px', margin: '0 4px' }}>
            1. Basic Info
          </div>
          <div style={{ flex: 1, textAlign: 'center', padding: '12px', borderRadius: '6px', background: step >= 2 ? '#667eea' : '#e2e8f0', color: step >= 2 ? 'white' : '#718096', fontWeight: 600, fontSize: '14px', margin: '0 4px' }}>
            2. Select Questions
          </div>
          <div style={{ flex: 1, textAlign: 'center', padding: '12px', borderRadius: '6px', background: step >= 3 ? '#667eea' : '#e2e8f0', color: step >= 3 ? 'white' : '#718096', fontWeight: 600, fontSize: '14px', margin: '0 4px' }}>
            3. Preview & Confirm
          </div>
        </div>

        {step === 1 && (
          <div>
            <h3 style={{ color: '#2d3748', fontSize: '18px', marginBottom: '20px', paddingBottom: '12px', borderBottom: '2px solid #e2e8f0' }}>
              Basic Information
            </h3>
            
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., JavaScript Interview Round 1"
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief description of the interview"
                rows="3"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Date & Time *</label>
                <input
                  type="datetime-local"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>End Date & Time *</label>
                <input
                  type="datetime-local"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Duration (minutes) *</label>
              <input
                type="number"
                name="duration"
                value={formData.duration}
                onChange={handleInputChange}
                min="5"
                max="240"
                required
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="allowMultipleTakes"
                  checked={formData.allowMultipleTakes}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({ 
                      ...prev, 
                      allowMultipleTakes: checked,
                      maxAttempts: checked ? prev.maxAttempts : 1
                    }));
                  }}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>Allow students to retake this interview multiple times</span>
              </label>
            </div>

            {formData.allowMultipleTakes && (
              <div className="form-group" style={{ marginLeft: '26px', marginTop: '-8px' }}>
                <label>Maximum Number of Attempts</label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="maxAttemptsType"
                      checked={formData.maxAttempts === 0}
                      onChange={() => setFormData(prev => ({ ...prev, maxAttempts: 0 }))}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>Unlimited</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="maxAttemptsType"
                      checked={formData.maxAttempts > 0}
                      onChange={() => setFormData(prev => ({ ...prev, maxAttempts: 2 }))}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>Specific number:</span>
                  </label>
                  {formData.maxAttempts > 0 && (
                    <input
                      type="number"
                      value={formData.maxAttempts}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 2;
                        setFormData(prev => ({ ...prev, maxAttempts: Math.max(2, value) }));
                      }}
                      min="2"
                      max="99"
                      style={{ 
                        width: '80px', 
                        padding: '6px 10px',
                        border: '1px solid #cbd5e0',
                        borderRadius: '4px'
                      }}
                    />
                  )}
                </div>
              </div>
            )}

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="showHints"
                  checked={formData.showHints}
                  onChange={(e) => setFormData(prev => ({ ...prev, showHints: e.target.checked }))}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>Show hints to students during the interview</span>
              </label>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="showAIEvaluation"
                  checked={formData.showAIEvaluation}
                  onChange={(e) => setFormData(prev => ({ ...prev, showAIEvaluation: e.target.checked }))}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>🤖 Enable AI analysis of interview responses</span>
              </label>
              <p style={{ fontSize: '12px', color: '#718096', marginTop: '6px', marginLeft: '26px' }}>
                AI will provide automated evaluation and feedback on student responses
              </p>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="requireVideoRecording"
                  checked={formData.requireVideoRecording}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({ 
                      ...prev, 
                      requireVideoRecording: checked,
                      // Auto-disable download if video recording is disabled
                      allowStudentDownload: checked ? prev.allowStudentDownload : false
                    }));
                  }}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>🎥 Require video recording for this interview</span>
              </label>
              <p style={{ fontSize: '12px', color: '#718096', marginTop: '6px', marginLeft: '26px' }}>
                {formData.requireVideoRecording 
                  ? 'Students must record video and audio while answering questions'
                  : 'Students can answer questions without video recording (text/voice only)'}
              </p>
            </div>

            {formData.requireVideoRecording && (
              <div className="form-group" style={{ marginLeft: '26px', marginTop: '-8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="allowStudentDownload"
                    checked={formData.allowStudentDownload}
                    onChange={(e) => setFormData(prev => ({ ...prev, allowStudentDownload: e.target.checked }))}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span>📥 Allow students to download their interview recordings</span>
                </label>
                <p style={{ fontSize: '12px', color: '#718096', marginTop: '6px', marginLeft: '26px' }}>
                  Students will be able to download their video recordings after submission
                </p>
              </div>
            )}

            <div className="form-group">
              <label>Difficulty Level</label>
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                {['beginner', 'intermediate', 'expert'].map(level => (
                  <label
                    key={level}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: `2px solid ${formData.difficulty === level ? '#667eea' : '#e2e8f0'}`,
                      background: formData.difficulty === level ? '#eef2ff' : 'white',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s',
                      fontWeight: formData.difficulty === level ? '600' : '400',
                      color: formData.difficulty === level ? '#667eea' : '#4a5568'
                    }}
                  >
                    <input
                      type="radio"
                      name="difficulty"
                      value={level}
                      checked={formData.difficulty === level}
                      onChange={handleInputChange}
                      style={{ display: 'none' }}
                    />
                    <div style={{ fontSize: '20px', marginBottom: '4px' }}>
                      {level === 'beginner' && '🟢'}
                      {level === 'intermediate' && '🟡'}
                      {level === 'expert' && '🔴'}
                    </div>
                    <div style={{ fontSize: '14px', textTransform: 'capitalize' }}>{level}</div>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Instructions</label>
              <textarea
                name="instructions"
                value={formData.instructions}
                onChange={handleInputChange}
                placeholder="Special instructions for candidates"
                rows="4"
              />
            </div>

            <div className="form-group">
              <label>Assignment Type *</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  className={formData.assignmentType === 'students' ? 'btn btn-primary' : 'btn btn-secondary'}
                  onClick={() => handleAssignmentTypeChange('students')}
                  style={{ flex: 1 }}
                >
                  Individual Students
                </button>
                <button
                  type="button"
                  className={formData.assignmentType === 'group' ? 'btn btn-primary' : 'btn btn-secondary'}
                  onClick={() => handleAssignmentTypeChange('group')}
                  style={{ flex: 1 }}
                >
                  Group
                </button>
              </div>
            </div>

            {formData.assignmentType === 'students' && (
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ margin: 0 }}>Select Students * {students.length > 0 && `(${students.length} available)`}</label>
                  <button 
                    type="button" 
                    onClick={fetchInitialData}
                    disabled={loadingStudents}
                    className="btn btn-secondary"
                    style={{ padding: '6px 12px', fontSize: '12px' }}
                  >
                    {loadingStudents ? '🔄 Loading...' : '🔄 Refresh'}
                  </button>
                </div>
                {loadingStudents ? (
                  <div style={{ padding: '12px', backgroundColor: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: '6px', color: '#096dd9', fontSize: '14px', textAlign: 'center' }}>
                    Loading students...
                  </div>
                ) : !students || students.length === 0 ? (
                  <div style={{ padding: '16px', backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: '6px', fontSize: '14px' }}>
                    <div style={{ fontWeight: 'bold', color: '#991b1b', marginBottom: '8px' }}>⚠️ No students available</div>
                    <div style={{ color: '#7f1d1d', fontSize: '13px', lineHeight: '1.5' }}>
                      Possible reasons:
                      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        <li>You need admin privileges to view students</li>
                        <li>Backend server may not be running (check port 3040)</li>
                        <li>No students have been added to the system yet</li>
                      </ul>
                      Try refreshing or contact an administrator.
                    </div>
                  </div>
                ) : (
                  <Select
                    key={`student-select-${students.length}-${formData.assignedTo.length}`}
                    isMulti
                    value={studentOptions.filter(opt => formData.assignedTo.includes(opt.value))}
                    options={studentOptions}
                    onChange={handleStudentSelection}
                    placeholder="Select students..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 1050 })
                    }}
                  />
                )}
              </div>
            )}

            {formData.assignmentType === 'group' && (
              <div className="form-group">
                <label>Select Group *</label>
                <Select
                  value={groupOptions.find(opt => opt.value === formData.assignedGroup)}
                  options={groupOptions}
                  onChange={handleGroupSelection}
                  placeholder="Select a group..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 1050 })
                  }}
                />
              </div>
            )}

            <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
              <button type="button" onClick={() => navigate('/interviews')} className="btn btn-secondary">
                Cancel
              </button>
              <button type="button" onClick={handleNext} className="btn btn-primary">
                Next: Select Questions
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h3 style={{ color: '#2d3748', fontSize: '18px', marginBottom: '20px', paddingBottom: '12px', borderBottom: '2px solid #e2e8f0' }}>
              Select Questions
            </h3>
            
            <div style={{ background: '#f7fafc', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search questions..."
                  style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
                />
                
                <Select
                  isMulti
                  options={tagOptions}
                  onChange={handleTagFilterChange}
                  placeholder="Filter by tags..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '40px',
                      fontSize: '13px',
                      borderColor: '#e2e8f0',
                      borderWidth: '1.5px'
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 1050 })
                  }}
                />

                <select
                  name="difficulty"
                  value={filters.difficulty}
                  onChange={handleFilterChange}
                  style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
                >
                  <option value="">All Difficulties</option>
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>

                <select
                  name="questionType"
                  value={filters.questionType}
                  onChange={handleFilterChange}
                  style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
                >
                  <option value="">All Types</option>
                  <option value="MCQ">MCQ</option>
                  <option value="TrueFalse">True/False</option>
                  <option value="OpenEnded">Open Ended</option>
                  <option value="CodeSnippet">Code Snippet</option>
                  <option value="CodeDragDrop">Code Drag & Drop</option>
                </select>

                <Select
                  isMulti
                  options={categoryOptions}
                  onChange={handleCategoryFilterChange}
                  placeholder="Filter by categories..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '40px',
                      fontSize: '13px',
                      borderColor: '#e2e8f0',
                      borderWidth: '1.5px'
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 1050 })
                  }}
                />

                <button onClick={fetchQuestions} className="btn btn-secondary" style={{ padding: '8px 16px', fontSize: '13px' }}>
                  Apply Filters
                </button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button onClick={handleSelectByTags} className="btn btn-primary" disabled={!filters.tags} style={{ fontSize: '13px' }}>
                  Auto-select by Tags
                </button>
                <span 
                  onClick={() => selectedQuestions.length > 0 && setShowQuestionsModal(true)}
                  style={{ 
                    fontWeight: 600, 
                    color: selectedQuestions.length > 0 ? '#667eea' : '#a0aec0', 
                    fontSize: '14px',
                    cursor: selectedQuestions.length > 0 ? 'pointer' : 'default',
                    textDecoration: selectedQuestions.length > 0 ? 'underline' : 'none',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedQuestions.length > 0) {
                      e.currentTarget.style.color = '#5568d3';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedQuestions.length > 0) {
                      e.currentTarget.style.color = '#667eea';
                    }
                  }}
                >
                  {selectedQuestions.length} question(s) selected {selectedQuestions.length > 0 && '👁️'}
                </span>
              </div>
            </div>

            <div style={{ maxHeight: '500px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
              {loading ? (
                <div>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} style={{
                      padding: '16px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      backgroundColor: '#fff'
                    }}>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <div style={{
                          width: '18px',
                          height: '18px',
                          backgroundColor: '#e2e8f0',
                          borderRadius: '4px',
                          flexShrink: 0,
                          animation: 'pulse 1.5s ease-in-out infinite',
                          animationDelay: `${i * 0.1}s`
                        }} />
                        <div style={{ flex: 1 }}>
                          <div style={{
                            width: '100%',
                            height: '18px',
                            backgroundColor: '#e2e8f0',
                            borderRadius: '6px',
                            marginBottom: '8px',
                            animation: 'pulse 1.5s ease-in-out infinite',
                            animationDelay: `${i * 0.1 + 0.1}s`
                          }} />
                          <div style={{
                            width: '85%',
                            height: '18px',
                            backgroundColor: '#e2e8f0',
                            borderRadius: '6px',
                            marginBottom: '12px',
                            animation: 'pulse 1.5s ease-in-out infinite',
                            animationDelay: `${i * 0.1 + 0.2}s`
                          }} />
                          <div style={{ display: 'flex', gap: '12px' }}>
                            <div style={{
                              width: '80px',
                              height: '24px',
                              backgroundColor: '#e2e8f0',
                              borderRadius: '12px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              animationDelay: `${i * 0.1 + 0.3}s`
                            }} />
                            <div style={{
                              width: '120px',
                              height: '24px',
                              backgroundColor: '#e2e8f0',
                              borderRadius: '12px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              animationDelay: `${i * 0.1 + 0.4}s`
                            }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <style>{`
                    @keyframes pulse {
                      0%, 100% { opacity: 1; }
                      50% { opacity: 0.5; }
                    }
                  `}</style>
                </div>
              ) : questions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>No questions found. Try adjusting filters.</div>
              ) : (
                questions.map(question => {
                  const isSelected = !!selectedQuestions.find(q => q._id === question._id);
                  return (
                    <div
                      key={question._id}
                      style={{
                        padding: '16px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        marginBottom: '12px',
                        background: isSelected ? '#f0f4ff' : 'white',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleQuestionToggle(question)}
                          style={{ marginTop: '4px', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                        />
                        <div 
                          style={{ flex: 1, cursor: 'pointer' }}
                          onClick={() => handleQuestionToggle(question)}
                        >
                          <h4 style={{ margin: '0 0 10px 0', color: '#2d3748', fontSize: '15px' }}>{question.question}</h4>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 600,
                              background: question.difficulty === 'Easy' ? '#c6f6d5' : question.difficulty === 'Medium' ? '#feebc8' : '#fed7d7',
                              color: question.difficulty === 'Easy' ? '#22543d' : question.difficulty === 'Medium' ? '#744210' : '#742a2a'
                            }}>
                              {question.difficulty}
                            </span>
                            <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: '#bee3f8', color: '#2c5282' }}>
                              {question.questionType}
                            </span>
                            {question.tags && question.tags.map((tag, tagIdx) => (
                              <span key={typeof tag === 'object' ? tag._id : tag || tagIdx} style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 500, background: '#e6fffa', color: '#234e52' }}>
                                {typeof tag === 'object' ? tag.name : tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
              <button type="button" onClick={handleBack} className="btn btn-secondary">
                Back
              </button>
              <button type="button" onClick={handleNext} className="btn btn-primary">
                Next: Preview
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h3 style={{ color: '#2d3748', fontSize: '18px', marginBottom: '20px', paddingBottom: '12px', borderBottom: '2px solid #e2e8f0' }}>
              Preview & Confirm
            </h3>
            
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ color: '#667eea', fontSize: '16px', marginBottom: '12px' }}>Interview Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', background: '#f7fafc', padding: '16px', borderRadius: '8px' }}>
                <div>
                  <strong style={{ color: '#718096', fontSize: '12px', display: 'block', marginBottom: '4px' }}>TITLE</strong>
                  <span style={{ color: '#2d3748', fontSize: '14px' }}>{formData.title}</span>
                </div>
                <div>
                  <strong style={{ color: '#718096', fontSize: '12px', display: 'block', marginBottom: '4px' }}>DESCRIPTION</strong>
                  <span style={{ color: '#2d3748', fontSize: '14px' }}>{formData.description || 'N/A'}</span>
                </div>
                <div>
                  <strong style={{ color: '#718096', fontSize: '12px', display: 'block', marginBottom: '4px' }}>START DATE</strong>
                  <span style={{ color: '#2d3748', fontSize: '14px' }}>{new Date(formData.startDate).toLocaleString()}</span>
                </div>
                <div>
                  <strong style={{ color: '#718096', fontSize: '12px', display: 'block', marginBottom: '4px' }}>END DATE</strong>
                  <span style={{ color: '#2d3748', fontSize: '14px' }}>{new Date(formData.endDate).toLocaleString()}</span>
                </div>
                <div>
                  <strong style={{ color: '#718096', fontSize: '12px', display: 'block', marginBottom: '4px' }}>DURATION</strong>
                  <span style={{ color: '#2d3748', fontSize: '14px' }}>{formData.duration} minutes</span>
                </div>
                <div>
                  <strong style={{ color: '#718096', fontSize: '12px', display: 'block', marginBottom: '4px' }}>MULTIPLE ATTEMPTS</strong>
                  <span style={{ color: '#2d3748', fontSize: '14px' }}>
                    {formData.allowMultipleTakes 
                      ? (formData.maxAttempts === 0 ? 'Unlimited' : `Up to ${formData.maxAttempts} attempts`)
                      : 'Not Allowed'
                    }
                  </span>
                </div>
                <div>
                  <strong style={{ color: '#718096', fontSize: '12px', display: 'block', marginBottom: '4px' }}>ASSIGNED TO</strong>
                  <span style={{ color: '#2d3748', fontSize: '14px' }}>
                    {formData.assignmentType === 'group'
                      ? groups.find(g => g._id === formData.assignedGroup)?.name || 'Group'
                      : `${formData.assignedTo.length} student(s)`}
                  </span>
                </div>
                <div>
                  <strong style={{ color: '#718096', fontSize: '12px', display: 'block', marginBottom: '4px' }}>AI EVALUATION</strong>
                  <span style={{ 
                    color: formData.showAIEvaluation ? '#22543d' : '#742a2a', 
                    fontSize: '14px',
                    fontWeight: 600,
                    padding: '4px 8px',
                    borderRadius: '4px',
                    background: formData.showAIEvaluation ? '#c6f6d5' : '#fed7d7',
                    display: 'inline-block'
                  }}>
                    {formData.showAIEvaluation ? '✓ Enabled' : '✗ Disabled'}
                  </span>
                </div>
                <div>
                  <strong style={{ color: '#718096', fontSize: '12px', display: 'block', marginBottom: '4px' }}>STUDENT DOWNLOAD</strong>
                  <span style={{ 
                    color: formData.allowStudentDownload ? '#22543d' : '#742a2a', 
                    fontSize: '14px',
                    fontWeight: 600,
                    padding: '4px 8px',
                    borderRadius: '4px',
                    background: formData.allowStudentDownload ? '#c6f6d5' : '#fed7d7',
                    display: 'inline-block'
                  }}>
                    {formData.allowStudentDownload ? '✓ Allowed' : '✗ Not Allowed'}
                  </span>
                </div>
                <div>
                  <strong style={{ color: '#718096', fontSize: '12px', display: 'block', marginBottom: '4px' }}>VIDEO RECORDING</strong>
                  <span style={{ 
                    color: formData.requireVideoRecording ? '#22543d' : '#744210', 
                    fontSize: '14px',
                    fontWeight: 600,
                    padding: '4px 8px',
                    borderRadius: '4px',
                    background: formData.requireVideoRecording ? '#c6f6d5' : '#fef3c7',
                    display: 'inline-block'
                  }}>
                    {formData.requireVideoRecording ? '🎥 Required' : '📝 Optional'}
                  </span>
                </div>
                <div>
                  <strong style={{ color: '#718096', fontSize: '12px', display: 'block', marginBottom: '4px' }}>DIFFICULTY LEVEL</strong>
                  <span style={{ 
                    color: '#2d3748', 
                    fontSize: '14px',
                    fontWeight: 600,
                    padding: '4px 10px',
                    borderRadius: '4px',
                    background: 
                      formData.difficulty === 'beginner' ? '#c6f6d5' :
                      formData.difficulty === 'intermediate' ? '#fef3c7' :
                      '#fed7d7',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    {formData.difficulty === 'beginner' && '🟢'}
                    {formData.difficulty === 'intermediate' && '🟡'}
                    {formData.difficulty === 'expert' && '🔴'}
                    <span style={{ textTransform: 'capitalize' }}>{formData.difficulty}</span>
                  </span>
                </div>
              </div>

              {formData.instructions && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                  <strong style={{ color: '#718096', fontSize: '12px', display: 'block', marginBottom: '8px' }}>INSTRUCTIONS</strong>
                  <p style={{ color: '#2d3748', fontSize: '14px', margin: 0 }}>{formData.instructions}</p>
                </div>
              )}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ color: '#667eea', fontSize: '16px', marginBottom: '8px' }}>Selected Questions ({selectedQuestions.length})</h4>
              <p style={{ fontSize: '12px', color: '#718096', marginBottom: '12px' }}>
                💡 Drag and drop questions to reorder them
              </p>
              <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
                {selectedQuestions.map((question, index) => (
                  <div
                    key={question._id}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    style={{
                      display: 'flex',
                      gap: '12px',
                      padding: '12px',
                      background: draggedIndex === index ? '#eef2ff' : dragOverIndex === index ? '#faf5ff' : '#f7fafc',
                      borderRadius: '6px',
                      marginBottom: '10px',
                      cursor: 'grab',
                      border: dragOverIndex === index ? '2px dashed #667eea' : '2px solid transparent',
                      transition: 'all 0.2s',
                      opacity: draggedIndex === index ? 0.5 : 1
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#a0aec0',
                      fontSize: '18px',
                      cursor: 'grab',
                      paddingRight: '8px'
                    }}>
                      ⋮⋮
                    </div>
                    <div style={{
                      width: '30px',
                      height: '30px',
                      background: '#667eea',
                      color: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      flexShrink: 0
                    }}>
                      {index + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: '8px', color: '#2d3748', fontWeight: 500, fontSize: '14px' }}>{question.question}</div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{
                          padding: '3px 8px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: question.difficulty === 'Easy' ? '#c6f6d5' : question.difficulty === 'Medium' ? '#feebc8' : '#fed7d7',
                          color: question.difficulty === 'Easy' ? '#22543d' : question.difficulty === 'Medium' ? '#744210' : '#742a2a'
                        }}>
                          {question.difficulty}
                        </span>
                        <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, background: '#bee3f8', color: '#2c5282' }}>
                          {question.questionType}
                        </span>
                        {question.tags && question.tags.slice(0, 3).map((tag, tagIdx) => (
                          <span key={typeof tag === 'object' ? tag._id : tag || tagIdx} style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 500, background: '#e6fffa', color: '#234e52' }}>
                            {typeof tag === 'object' ? tag.name : tag}
                          </span>
                        ))}
                      </div>
                      {/* Follow-up Questions Toggle */}
                      <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <label style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          cursor: 'pointer',
                          padding: '8px 10px',
                          background: followUpEnabled[question._id] ? '#f0f4ff' : '#f7fafc',
                          borderRadius: '6px',
                          border: followUpEnabled[question._id] ? '1px solid #667eea' : '1px solid #e2e8f0',
                          width: 'fit-content'
                        }}>
                          <input
                            type="checkbox"
                            checked={followUpEnabled[question._id] || false}
                            onChange={() => toggleFollowUp(question._id)}
                            style={{ cursor: 'pointer' }}
                          />
                          <span style={{ 
                            fontSize: '12px', 
                            color: followUpEnabled[question._id] ? '#667eea' : '#718096',
                            fontWeight: followUpEnabled[question._id] ? 600 : 500
                          }}>
                            🤖 Enable AI Follow-up Questions
                          </span>
                        </label>
                        
                        {/* Show range inputs when follow-up is enabled */}
                        {followUpEnabled[question._id] && (
                          <div style={{ 
                            padding: '6px 12px',
                            background: '#f0f4ff',
                            border: '1px solid #c7d2fe',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ fontSize: '11px', color: '#667eea', fontWeight: 600 }}>Range:</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <label style={{ fontSize: '10px', color: '#4c5563', fontWeight: 500 }}>Min</label>
                              <input
                                type="number"
                                min="0"
                                max="5"
                                value={followUpRanges[question._id]?.min ?? 1}
                                onChange={(e) => {
                                  const val = Math.max(0, Math.min(5, parseInt(e.target.value) || 0));
                                  const currentMax = followUpRanges[question._id]?.max ?? 2;
                                  updateFollowUpRange(question._id, 'min', val);
                                  if (val > currentMax) {
                                    updateFollowUpRange(question._id, 'max', val);
                                  }
                                }}
                                style={{
                                  width: '45px',
                                  padding: '3px 5px',
                                  border: '1px solid #c7d2fe',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  textAlign: 'center'
                                }}
                              />
                            </div>
                            <span style={{ color: '#94a3b8', fontSize: '11px' }}>-</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <label style={{ fontSize: '10px', color: '#4c5563', fontWeight: 500 }}>Max</label>
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={followUpRanges[question._id]?.max ?? 2}
                                onChange={(e) => {
                                  const val = Math.max(1, Math.min(10, parseInt(e.target.value) || 1));
                                  const currentMin = followUpRanges[question._id]?.min ?? 1;
                                  updateFollowUpRange(question._id, 'max', val);
                                  if (val < currentMin) {
                                    updateFollowUpRange(question._id, 'min', val);
                                  }
                                }}
                                style={{
                                  width: '45px',
                                  padding: '3px 5px',
                                  border: '1px solid #c7d2fe',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  textAlign: 'center'
                                }}
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* Nested Follow-up (only show if regular follow-up is enabled) */}
                        {followUpEnabled[question._id] && (
                          <div style={{ marginTop: '8px', marginLeft: '0px' }}>
                            <label style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px', 
                              cursor: 'pointer',
                              padding: '8px 10px',
                              background: nestedFollowUpEnabled[question._id] ? '#faf5ff' : '#f7fafc',
                              borderRadius: '6px',
                              border: nestedFollowUpEnabled[question._id] ? '1px solid #9f7aea' : '1px solid #e2e8f0',
                              width: 'fit-content'
                            }}>
                              <input
                                type="checkbox"
                                checked={nestedFollowUpEnabled[question._id] || false}
                                onChange={() => toggleNestedFollowUp(question._id)}
                                style={{ cursor: 'pointer' }}
                              />
                              <span style={{ 
                                fontSize: '12px', 
                                color: nestedFollowUpEnabled[question._id] ? '#9f7aea' : '#718096',
                                fontWeight: nestedFollowUpEnabled[question._id] ? 600 : 500
                              }}>
                                🔄 Enable Nested Follow-ups (Follow-ups for Follow-ups)
                              </span>
                            </label>
                            
                            {/* Show range inputs when nested follow-up is enabled */}
                            {nestedFollowUpEnabled[question._id] && (
                              <div style={{ 
                                marginTop: '8px',
                                padding: '6px 12px',
                                background: '#faf5ff',
                                border: '1px solid #d6bcfa',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <span style={{ fontSize: '11px', color: '#9f7aea', fontWeight: 600 }}>Nested Range:</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <label style={{ fontSize: '10px', color: '#4c5563', fontWeight: 500 }}>Min</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="3"
                                    value={nestedFollowUpRanges[question._id]?.min ?? 1}
                                    onChange={(e) => {
                                      const val = Math.max(0, Math.min(3, parseInt(e.target.value) || 0));
                                      const currentMax = nestedFollowUpRanges[question._id]?.max ?? 1;
                                      updateNestedFollowUpRange(question._id, 'min', val);
                                      if (val > currentMax) {
                                        updateNestedFollowUpRange(question._id, 'max', val);
                                      }
                                    }}
                                    style={{
                                      width: '45px',
                                      padding: '3px 5px',
                                      border: '1px solid #d6bcfa',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      textAlign: 'center'
                                    }}
                                  />
                                </div>
                                <span style={{ color: '#94a3b8', fontSize: '11px' }}>-</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <label style={{ fontSize: '10px', color: '#4c5563', fontWeight: 500 }}>Max</label>
                                  <input
                                    type="number"
                                    min="1"
                                    max="5"
                                    value={nestedFollowUpRanges[question._id]?.max ?? 1}
                                    onChange={(e) => {
                                      const val = Math.max(1, Math.min(5, parseInt(e.target.value) || 1));
                                      const currentMin = nestedFollowUpRanges[question._id]?.min ?? 1;
                                      updateNestedFollowUpRange(question._id, 'max', val);
                                      if (val < currentMin) {
                                        updateNestedFollowUpRange(question._id, 'min', val);
                                      }
                                    }}
                                    style={{
                                      width: '45px',
                                      padding: '3px 5px',
                                      border: '1px solid #d6bcfa',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      textAlign: 'center'
                                    }}
                                  />
                                </div>
                                <span style={{ fontSize: '10px', color: '#6b46c1', fontStyle: 'italic' }}>
                                  (Per follow-up question)
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: '#fffaf0', border: '1px solid #fbd38d', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
              <strong style={{ color: '#744210', display: 'block', marginBottom: '6px' }}>⚠️ Important:</strong>
              <span style={{ color: '#744210', fontSize: '14px' }}>
                Once created, questions cannot be modified. Students will be assigned this interview and notified.
              </span>
            </div>

            <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
              <button type="button" onClick={handleBack} className="btn btn-secondary">
                Back to Questions
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Interview' : 'Create Interview')}
              </button>
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* Selected Questions Modal */}
      <Modal
        open={showQuestionsModal}
        onClose={() => setShowQuestionsModal(false)}
        aria-labelledby="selected-questions-modal-title"
        aria-describedby="selected-questions-modal-description"
        disableScrollLock={true}
        BackdropProps={{
          timeout: 0
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '5%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: '95%', sm: '90%', md: '900px' },
            maxHeight: '90vh',
            minHeight: '300px',
            bgcolor: 'background.paper',
            borderRadius: '12px',
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            outline: 'none'
          }}
        >
          {/* Modal Header */}
          <div style={{
            padding: '24px',
            borderBottom: '2px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#f7fafc'
          }}>
            <div>
              <h3 id="selected-questions-modal-title" style={{ margin: 0, color: '#2d3748', fontSize: '20px', fontWeight: '700' }}>
                Selected Questions ({selectedQuestions.length})
              </h3>
              <p style={{ margin: '4px 0 0 0', color: '#718096', fontSize: '13px' }}>
                💡 Drag and drop to reorder • Click ✕ to remove
              </p>
            </div>
            <IconButton
              aria-label="close"
              onClick={() => setShowQuestionsModal(false)}
              sx={{
                color: '#a0aec0',
                '&:hover': {
                  backgroundColor: '#fee',
                  color: '#e53e3e'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>

            {/* Modal Body */}
            <div style={{
              padding: '24px',
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: 1,
              WebkitOverflowScrolling: 'touch',
              scrollBehavior: 'smooth',
              minHeight: 0
            }}>
              {selectedQuestions.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#a0aec0'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
                  <p style={{ fontSize: '16px', margin: 0 }}>No questions selected yet</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedQuestions.map((question, index) => (
                    <div
                      key={question._id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      style={{
                        display: 'flex',
                        gap: '12px',
                        padding: '16px',
                        background: draggedIndex === index ? '#eef2ff' : dragOverIndex === index ? '#faf5ff' : '#f7fafc',
                        borderRadius: '8px',
                        border: dragOverIndex === index ? '2px dashed #667eea' : '2px solid #e2e8f0',
                        cursor: 'grab',
                        transition: 'all 0.2s',
                        opacity: draggedIndex === index ? 0.5 : 1,
                        position: 'relative'
                      }}
                    >
                      {/* Drag Handle */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#a0aec0',
                        fontSize: '18px',
                        cursor: 'grab',
                        paddingRight: '8px'
                      }}>
                        ⋮⋮
                      </div>

                      {/* Question Number */}
                      <div style={{
                        width: '36px',
                        height: '36px',
                        background: '#667eea',
                        color: 'white',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '15px',
                        flexShrink: 0
                      }}>
                        {index + 1}
                      </div>

                      {/* Question Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          marginBottom: '8px',
                          color: '#2d3748',
                          fontWeight: 500,
                          fontSize: '14px',
                          lineHeight: '1.5'
                        }}>
                          {question.question}
                        </div>
                        
                        {/* Question Metadata */}
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: question.difficulty === 'Easy' ? '#c6f6d5' : 
                                       question.difficulty === 'Medium' ? '#feebc8' : '#fed7d7',
                            color: question.difficulty === 'Easy' ? '#22543d' : 
                                   question.difficulty === 'Medium' ? '#744210' : '#742a2a'
                          }}>
                            {question.difficulty}
                          </span>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: '#bee3f8',
                            color: '#2c5282'
                          }}>
                            {question.questionType}
                          </span>
                          {question.tags && question.tags.slice(0, 3).map((tag, tagIdx) => (
                            <span
                              key={typeof tag === 'object' ? tag._id : tag || tagIdx}
                              style={{
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 500,
                                background: '#e6fffa',
                                color: '#234e52'
                              }}
                            >
                              {typeof tag === 'object' ? tag.name : tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => handleQuestionToggle(question)}
                        style={{
                          background: '#fee',
                          border: 'none',
                          color: '#e53e3e',
                          cursor: 'pointer',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '18px',
                          fontWeight: 700,
                          width: '36px',
                          height: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#feb2b2';
                          e.currentTarget.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#fee';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        title="Remove question"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '20px 24px',
              borderTop: '2px solid #e2e8f0',
              background: '#f7fafc',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ color: '#718096', fontSize: '14px' }}>
                <strong style={{ color: '#2d3748' }}>{selectedQuestions.length}</strong> questions selected
              </div>
              <button
                onClick={() => setShowQuestionsModal(false)}
                className="btn btn-primary"
                style={{ fontSize: '14px', padding: '10px 24px' }}
              >
                Done
              </button>
            </div>
          </Box>
        </Modal>
    </div>
  );
}

export default ScheduleInterview;
